import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, MoreVertical, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  materialTheme, 
  getMetricCardTheme, 
  type MetricCardTheme 
} from '@/lib/material-design-tokens';
import { MaterialCard } from '../layout/MaterialDashboardGrid';

interface TrendData {
  value: number;
  direction: 'up' | 'down' | 'neutral';
  label: string;
  isPercentage?: boolean;
}

interface MaterialMetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  theme: MetricCardTheme;
  trend?: TrendData;
  loading?: boolean;
  onClick?: () => void;
  showMenu?: boolean;
  onMenuClick?: () => void;
  className?: string;
  sparklineData?: number[];
  // Trial mode props
  isTrialMode?: boolean;
  showSparkle?: boolean;
  onSparkleClick?: () => void;
  ctaText?: string;
  onCtaClick?: () => void;
  showCta?: boolean;
  compact?: boolean;
}

// Animation variants for the card content
const cardContentVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: {
      duration: 0.4,
      ease: materialTheme.motion.curves.standard,
    }
  },
};

// Animation variants for the metric value
const valueVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5,
      ease: materialTheme.motion.curves.decelerate,
      delay: 0.2,
    }
  },
};

// Loading skeleton component
const MetricCardSkeleton: React.FC = () => (
  <MaterialCard className="animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
        <div className="space-y-2">
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
          <div className="h-8 w-16 bg-gray-200 rounded"></div>
        </div>
      </div>
      <div className="w-6 h-6 bg-gray-200 rounded"></div>
    </div>
    <div className="mt-4 flex items-center space-x-2">
      <div className="h-4 w-4 bg-gray-200 rounded"></div>
      <div className="h-3 w-16 bg-gray-200 rounded"></div>
    </div>
  </MaterialCard>
);

// Trend indicator component
const TrendIndicator: React.FC<{ trend: TrendData; themeColors: any }> = ({ 
  trend, 
  themeColors 
}) => {
  const isPositive = trend.direction === 'up';
  const isNegative = trend.direction === 'down';
  const isNeutral = trend.direction === 'neutral';

  const trendColor = isPositive 
    ? materialTheme.colors.success[600]
    : isNegative 
    ? materialTheme.colors.error[600]
    : materialTheme.colors.neutral[500];

  const TrendIcon = isPositive ? TrendingUp : isNegative ? TrendingDown : null;

  return (
    <motion.div 
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.4 }}
      className="flex items-center space-x-2"
    >
      {TrendIcon && (
        <TrendIcon 
          className="w-4 h-4" 
          style={{ color: trendColor }}
        />
      )}
      <span 
        className="text-sm font-medium"
        style={{ 
          color: trendColor,
          ...materialTheme.typography.labelMedium,
        }}
      >
        {trend.isPercentage ? `${trend.value}%` : trend.value}
      </span>
      <span 
        className="text-xs"
        style={{ 
          color: materialTheme.colors.neutral[600],
          ...materialTheme.typography.bodySmall,
        }}
      >
        {trend.label}
      </span>
    </motion.div>
  );
};

// Mini sparkline component
const MiniSparkline: React.FC<{ data: number[]; color: string }> = ({ 
  data, 
  color 
}) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 60; // 60px width
    const y = 20 - ((value - min) / range) * 20; // 20px height, inverted
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width="60" height="20" className="opacity-60">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        points={points}
        className="drop-shadow-sm"
      />
    </svg>
  );
};

export const MaterialMetricCard: React.FC<MaterialMetricCardProps> = ({
  title,
  value,
  icon,
  theme,
  trend,
  loading = false,
  onClick,
  showMenu = false,
  onMenuClick,
  className,
  sparklineData,
  // Trial mode props
  isTrialMode = false,
  showSparkle = false,
  onSparkleClick,
  ctaText,
  onCtaClick,
  showCta = false,
  compact = false,
}) => {
  const themeColors = getMetricCardTheme(theme);

  // Show loading skeleton
  if (loading) {
    return <MetricCardSkeleton />;
  }

  return (
    <MaterialCard
      hover={!!onClick}
      clickable={!!onClick}
      onClick={onClick}
      className={cn(
        'relative overflow-hidden',
        compact ? 'p-4' : 'p-6', // Reduced padding for compact mode
        className
      )}
    >
      <motion.div
        variants={cardContentVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header with icon and menu/sparkle */}
        <div className={cn("flex items-start justify-between", compact ? "mb-3" : "mb-4")}>
          {/* Icon with themed background */}
          <motion.div
            className={cn(
              "flex items-center justify-center rounded-full",
              compact ? "w-10 h-10" : "w-12 h-12"
            )}
            style={{
              backgroundColor: themeColors.background,
              color: themeColors.icon,
            }}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            {React.cloneElement(icon as React.ReactElement, {
              className: compact ? 'w-5 h-5' : 'w-6 h-6',
            })}
          </motion.div>

          {/* Sparkle button for trial mode */}
          {showSparkle && (
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                onSparkleClick?.();
              }}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              title="See advanced analytics coming soon ✨"
            >
              <Sparkles className="w-4 h-4 text-yellow-500" />
            </motion.button>
          )}

          {/* Menu button */}
          {showMenu && !showSparkle && (
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                onMenuClick?.();
              }}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </motion.button>
          )}
        </div>

        {/* Title and Value - Centered */}
        <div className={cn("space-y-2 text-center", compact && "space-y-1")}>
          <h3 
            className={cn("font-medium text-gray-600", compact ? "text-xs" : "text-sm")}
            style={materialTheme.typography.labelMedium}
          >
            {title}
          </h3>
          
          <motion.div
            variants={valueVariants}
            className="flex flex-col items-center justify-center"
          >
            <span 
              className={cn("font-bold", compact ? "text-2xl" : "text-3xl")}
              style={{
                color: themeColors.text,
                ...materialTheme.typography.headlineMedium,
              }}
            >
              {typeof value === 'number' ? value.toLocaleString() : value}
            </span>
            
            {/* Mini sparkline if data provided */}
            {sparklineData && sparklineData.length > 1 && (
              <div className="mt-1">
                <MiniSparkline 
                  data={sparklineData} 
                  color={themeColors.icon} 
                />
              </div>
            )}
          </motion.div>
        </div>

        {/* Trend indicator */}
        {trend && !isTrialMode && (
          <div className="mt-4">
            <TrendIndicator trend={trend} themeColors={themeColors} />
          </div>
        )}

        {/* Trial mode bottom content */}
        {isTrialMode && (
          <div className={cn(compact ? "mt-3" : "mt-4")}>
            {showCta && ctaText ? (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onCtaClick?.();
                }}
                className="w-full px-4 py-2 text-sm font-medium rounded-lg border-2 border-dashed transition-colors"
                style={{ 
                  borderColor: themeColors.icon + '40',
                  color: themeColors.icon,
                  backgroundColor: themeColors.background
                }}
              >
                {ctaText}
              </motion.button>
            ) : (
              // When they have data, show dynamic encouraging message
              <div className="text-center">
                <div className="flex items-center justify-center text-xs text-green-600 gap-1">
                  {/* Dynamic messaging based on card title and value */}
                  {title.includes('Students') && (
                    <>
                      <span>🌟</span>
                      <span>
                        {typeof value === 'number' && value >= 10 
                          ? "Amazing community you're building!" 
                          : typeof value === 'number' && value >= 5
                          ? "Your student family is growing!"
                          : "Great start! Keep adding students!"
                        }
                      </span>
                    </>
                  )}
                  {title.includes('Revenue') && (
                    <>
                      <span>💰</span>
                      <span>Money flowing in! You're doing great!</span>
                    </>
                  )}
                  {title.includes('Classes') && (
                    <>
                      <span>📚</span>
                      <span>
                        {typeof value === 'number' && value >= 3
                          ? "Diverse program offerings!" 
                          : "Solid class foundation!"
                        }
                      </span>
                    </>
                  )}
                  {/* Default fallback */}
                  {!title.includes('Students') && !title.includes('Revenue') && !title.includes('Classes') && (
                    <>
                      <span>🎉</span>
                      <span>You're building something great!</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Subtle background decoration */}
        <div 
          className="absolute -right-4 -bottom-4 w-16 h-16 rounded-full opacity-5"
          style={{ backgroundColor: themeColors.icon }}
        />
      </motion.div>

      {/* Hover effect overlay */}
      {onClick && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r opacity-0 hover:opacity-5 transition-opacity duration-300"
          style={{
            background: `linear-gradient(135deg, ${themeColors.icon}20, ${themeColors.icon}10)`,
          }}
        />
      )}
    </MaterialCard>
  );
};

// Pre-configured metric card variants for common use cases
export const StudentMetricCard: React.FC<Omit<MaterialMetricCardProps, 'theme'>> = (props) => (
  <MaterialMetricCard {...props} theme="primary" />
);

export const RevenueMetricCard: React.FC<Omit<MaterialMetricCardProps, 'theme'>> = (props) => (
  <MaterialMetricCard {...props} theme="success" />
);

export const AttendanceMetricCard: React.FC<Omit<MaterialMetricCardProps, 'theme'>> = (props) => (
  <MaterialMetricCard {...props} theme="warning" />
);

export const GrowthMetricCard: React.FC<Omit<MaterialMetricCardProps, 'theme'>> = (props) => (
  <MaterialMetricCard {...props} theme="secondary" />
);

export default MaterialMetricCard;